import { Button } from '@thanx/uikit/button'
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { getUserProfile } from 'actions/users'
import { storedValueApi } from 'api/storedValue'
import Badge from 'components/Badge'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import Spinner from 'components/Spinner'
import TabsContainer, { TabT } from 'components/Tabs/Container'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { usePermissionReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { Container, Row } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { selectCurrentMerchant } from 'selectors/merchant'
import { selectExperience } from 'selectors/pointsExperience'
import { selectUserProfile } from 'selectors/userProfile'
import { history } from 'store'
import { merchantTheme } from 'theme'
import { useAbility } from 'utilities/ability'
import { getFormattedDate } from 'utilities/date'
import { useUserTimeZone } from 'utilities/userTimeZone'
import ActionsMenu from './ActionsMenu'
import Activity from './Activity'
import Cards from './Cards'
import GiftCards from './GiftCards'
import GrantModal from './GrantModal'
import { getFullName } from './helpers'
import Profile from './Profile'
import StoredValue from './StoredValue'
import TierBadge from './TierBadge'
import useProfileTiers from './useProfileTiers'

const t = buildTranslate('users.view')

const ALL_TABS = [
  'activity',
  'profile',
  'cards',
  'gift_cards',
  'stored_value',
] as const
type Tab = typeof ALL_TABS[number]

type PropsT = {
  match: {
    params: {
      identifier: string
    }
  }
}

const isTab = (tab: unknown): tab is Tab => ALL_TABS.includes(tab as Tab)

const View: React.FC<PropsT> = ({ match }) => {
  const identifier = match.params.identifier

  const [css] = useStyletron()
  const storedValueEnabled = useFlag('display-stored-value-mx', false)
  const giftCardsEnabled = useFlag('gift-cards-mx', false)
  const merchant = useSelector(selectCurrentMerchant)
  const userTimeZone = useUserTimeZone()
  const [isFetchingProfile, setIsFetchingProfile] = useState(false)
  const [showGrantModal, setShowGrantModal] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const param = history.location.hash.replace('#', '') as Tab
    if (isTab(param)) {
      return param
    }
    return 'profile'
  })
  const { data: storedValueData } = storedValueApi.useGetConfigQuery(
    undefined,
    { skip: !storedValueEnabled }
  )
  const hasStoredValue =
    storedValueEnabled && storedValueData?.state === 'active'

  const dispatch = useDispatch()
  useEffect(() => {
    async function fetchProfile() {
      setIsFetchingProfile(true)
      await dispatch(getUserProfile(identifier))
      setIsFetchingProfile(false)
    }

    fetchProfile()
  }, [dispatch, identifier])

  const profile = useSelector(state => selectUserProfile(state, identifier))
  useProfileTiers()

  const title = (() => {
    if (!profile) return t('title')

    const name = getFullName(profile)
    return name || t('title')
  })()

  const ability = useAbility()
  // The modal grants a reward OR points, and each takes its own permission,
  // the same on the FE and in the API. A reward takes manage Reward Issuance,
  // the gate on POST users/:id/grant_reward. Points take manage Users, the
  // gate on grant_points, plus a points experience to grant into (Counters
  // loads it on both default tabs). Granting a reward takes the same
  // permission on every surface: the order-feedback rating detail and the
  // RecoveryAI case detail also gate their Grant reward on RewardIssuance.
  // The other API endpoints that still accept manage members for a reward
  // grant are tracked in LOY-1107.
  const pointsExperience = useSelector(selectExperience)
  const canGrantReward = ability.can('manage', 'RewardIssuance')
  const canGrantPoints = ability.can('manage', 'Users') && !!pointsExperience
  const cannotGrant = !canGrantReward && !canGrantPoints
  const grantRewardReason = usePermissionReason('RewardIssuance', cannotGrant)

  const tabs: TabT[] = [
    {
      id: 'activity',
      name: t('activity.title'),
      content: <Activity profile={profile} />,
      disabled: ability.cannot('access', 'Users'),
    },
    {
      id: 'profile',
      name: t('profile.title'),
      content: <Profile profile={profile} />,
    },
  ]

  if (profile) {
    tabs.push({
      id: 'cards',
      name: t('cards.title'),
      content: <Cards profile={profile} />,
    })
  }

  if (giftCardsEnabled && merchant.gift_cards_enabled && profile) {
    tabs.push({
      id: 'gift_cards',
      name: t('gift_cards.title'),
      content: <GiftCards profile={profile} />,
    })
  }

  if (hasStoredValue && profile) {
    tabs.push({
      id: 'stored_value',
      name: t('stored_value.title'),
      content: <StoredValue profile={profile} />,
    })
  }

  return (
    <Container fluid className="p-0 white-bkg">
      <Helmet>
        <title>{t(`${activeTab}.page_title`)}</title>
      </Helmet>
      <Row className="mb-s ml-s mt-s">
        <Link
          className="grey-90 grey-10-hover-bkg grey-90-hover bold border-radius-5 no-underline"
          to={(history.location.state as any)?.returnTo || '/users'}
        >
          <i className="mr-s fa fa-arrow-left grey-90" title={t('back')} />
          {t('back')}
        </Link>
      </Row>
      <Container>
        <div className="d-flex justify-content-between mb-m">
          <div>
            <Switch condition={profile?.membership_state === 'disabled'}>
              <div className={'mb-xs'}>
                <Badge
                  value={t('blocked')}
                  className={`${css({
                    backgroundColor: merchantTheme.colors.cayenne10,
                    color: merchantTheme.colors.cayenne50,
                  })}`}
                />
              </div>
            </Switch>
            <Spinner
              className={'d-flex justify-content-center'}
              isLoading={isFetchingProfile}
            >
              <Text.Header2 className="m-0 mr-xl">{title}</Text.Header2>
            </Spinner>
            {profile?.membership_created_at && (
              <div className={'mt-xs'}>
                <Text.BodyText3>
                  {t('member_since', {
                    date: getFormattedDate(
                      moment.tz(profile.membership_created_at, userTimeZone),
                      'daily'
                    ),
                  })}
                </Text.BodyText3>
              </div>
            )}
          </div>
          <div>
            <div
              className={`d-flex align-items-center ${css({
                columnGap: '16px',
                justifyContent: 'space-between',
                marginRight: '-16px',
                marginLeft: '-16px',
              })}`}
            >
              {!!profile && <ActionsMenu profile={profile} />}
              {/* `inline`: PermissionTooltip's wrapper defaults to
                  `d-block w-100`, which inside this flex row stretches the
                  disabled button across it (and away from the actions menu it
                  sits beside). Only visible once the button actually disables,
                  which is why it went unnoticed. */}
              <PermissionTooltip
                disabled={cannotGrant}
                reason={grantRewardReason}
                id="grant-reward-perm"
                placement="bottom"
                inline
              >
                <Button
                  onClick={() => setShowGrantModal(true)}
                  disabled={cannotGrant}
                >
                  {t('grant_reward')}
                </Button>
              </PermissionTooltip>
            </div>
          </div>
        </div>
        <div>{profile && <TierBadge profile={profile} />}</div>
      </Container>
      <Spinner
        className={'d-flex justify-content-center'}
        size="3x"
        isLoading={isFetchingProfile}
      >
        <TabsContainer
          defaultTab={activeTab}
          shouldReplaceHash
          onSelect={tab => setActiveTab(tab as Tab)}
          tabs={tabs}
        />
      </Spinner>
      {/* `&& !cannotGrant` is defense-in-depth: the trigger already can't
          open this for an unauthorized viewer, but keying the mount on the
          same gate is what actually stops one, by any route — mirrors
          RatingDetail.tsx and RecoveryAI/CaseDetail's own grant-reward
          modals. GrantModal itself only offers the options the viewer can
          grant. */}
      {!!profile && !cannotGrant && (
        <GrantModal
          visible={showGrantModal}
          onClose={() => setShowGrantModal(false)}
          profile={profile}
        />
      )}
    </Container>
  )
}

export default View
